/**
 * FilePreviewModal — выбор действия + быстрый просмотр.
 *
 * Сначала показываем карточку файла с двумя действиями:
 *   👁 Посмотреть    → inline read-only preview: на ПК — PDF в iframe,
 *                      на телефоне — DocPagesViewer (все страницы лентой,
 *                      серверный рендер; iframe там показывает 1 страницу/ничего)
 *   ✏️ Редактировать → открывает OnlyOffice в НОВОЙ ВКЛАДКЕ (стартует мгновенно;
 *                      Community-редакция на телефоне открывает только просмотр)
 *
 * При клике «Посмотреть» — модалка переходит в режим просмотра ниже.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { copyDoc, fetchDocRestrictions, setDocRestrictions, renameAttachment } from "@/api/drive";

import { Icon } from "@/components/prototype/Icon";
import { useNavigate } from "@tanstack/react-router";
import { DocPagesViewer } from "@/components/DocPagesViewer";
import { SheetViewer } from "@/components/SheetViewer";
import { Spinner } from "@/components/Spinner";


type ViewKind = "pdf" | "text" | "office" | "spreadsheet" | "other";
type Mode = "choice" | "view";


function detectKind(filename: string, mime: string | null | undefined): ViewKind {
  const m = (mime || "").toLowerCase();
  const f = filename.toLowerCase();
  if (m === "application/pdf" || f.endsWith(".pdf")) return "pdf";
  // Spreadsheets (xlsx/xls/ods/csv) — рендерим через OnlyOffice (нативно как Excel).
  // LibreOffice-PDF превью даёт огромные ячейки с криво выровненным текстом.
  if (/\.(xlsx?|ods|csv)$/i.test(f) || m.includes("spreadsheetml") || m.includes("ms-excel")) {
    return "spreadsheet";
  }
  // DOCX/PPTX/прочие office → серверный preview-pdf (LibreOffice конвертит)
  if (/\.(docx?|pptx?|odt|odp|rtf)$/i.test(f) || m.includes("wordprocessingml")) return "office";
  if (m.startsWith("text/") || /\.(txt|md|json|log|xml|yaml|yml|js|ts|tsx|jsx|py|css|scss|html|sh|sql|env|conf)$/i.test(f)) return "text";
  return "other";
}

function isManagedOfficeDocument(filename: string, mime: string | null | undefined): boolean {
  const f = filename.toLowerCase();
  const m = (mime || "").toLowerCase();
  return /\.(docx|doc|odt|rtf|xlsx|xls|ods|csv|pptx|ppt|odp|pdf)$/i.test(f)
    || m === "application/pdf";
}

// Диск внутри CRM (своя сборка под /crm/): редакторов Word/Excel там нет — они в мессенджере на его поддомене.
// Открываем их там, в новой вкладке; в мессенджере — обычный переход (29.09.2026).
const IN_CRM = import.meta.env.BASE_URL.startsWith("/crm");
function openInMessenger(path: string): void {
  const h = window.location.hostname;
  const host = h.startsWith("messenger.") ? h : `messenger.${h}`;
  window.open(`https://${host}${path}`, "_blank", "noopener");
}

function officePreviewUrl(downloadUrl: string): string {
  return downloadUrl.replace(/\/download(\?|$)/, "/preview-pdf$1");
}

function forceDownloadUrl(url: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}download=1`;
}

function fmtSize(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

function fileTypeColor(filename: string): string {
  const f = filename.toLowerCase();
  if (f.endsWith(".pdf")) return "#d93025";
  if (/\.(docx?|odt|rtf)$/.test(f)) return "#1a73e8";
  if (/\.(xlsx?|csv|ods)$/.test(f)) return "#0b8043";
  if (/\.(pptx?|odp)$/.test(f)) return "#f9ab00";
  return "#5f6368";
}

function fileTypeLabel(filename: string): string {
  const ext = filename.split(".").pop()?.toUpperCase() || "";
  return ext;
}


export function FilePreviewModal({
  url, filename, mime, sizeBytes, onClose, attachmentId, onSaveToDrive,
  onDelete, onCopied, onRenamed,
}: {
  url: string;
  filename: string;
  mime: string | null;
  sizeBytes?: number | null;
  onClose: () => void;
  attachmentId?: number;
  onSaveToDrive?: (img: { attachmentId: number; filename?: string }) => void;
  /** Если задано — показывает кнопку «Перейти к папке» (DriveView при фильтрации). */
  goToFolder?: { label: string; onClick: () => void };
  /** Если задано — показывает кнопку «Удалить» (отправляет в корзину Диска). */
  onDelete?: () => void;
  /** Колбэк после успешного «Копировать» — фронт может обновить список. */
  onCopied?: (newAttachmentId: number, newFilename: string) => void;
  /** Колбэк после успешного «Переименовать» — фронт может обновить список/кэш. */
  onRenamed?: (newFilename: string) => void;
}) {
  const [renameOpen, setRenameOpen] = useState(false);
  const kind = detectKind(filename, mime);
  const isManagedDocument = isManagedOfficeDocument(filename, mime) && attachmentId != null;
  // Для PDF тоже сохраняем привычный выбор «Смотреть / Редактировать».
  // OnlyOffice открывает PDF своим PDF-редактором (формы/аннотации),
  // тогда как обычный просмотр по-прежнему использует надёжный PDF viewer.
  // Правка — только docx/xlsx, они открываются в редакторе «Страницы».
  // pdf/pptx — только просмотр (OnlyOffice больше не используется).
  const canEdit = isManagedDocument && /\.(docx|xlsx)$/i.test(filename);
  // По дефолту: если файл редактируемый — показываем выбор;
  // иначе сразу превью (картинки/код/архивы и т.п. не редактируются).
  const [mode, setMode] = useState<Mode>(canEdit ? "choice" : "view");

  // Запреты на документ (download/copy). Тянем для всех редактируемых
  // документов — UI скрывает/деактивирует соответствующие кнопки.
  const restrictionsQ = useQuery({
    queryKey: ["docs/restrictions", attachmentId],
    queryFn: () => fetchDocRestrictions(attachmentId!),
    enabled: attachmentId != null && isManagedDocument,
    staleTime: 30_000,
  });
  const downloadBlocked = !!restrictionsQ.data?.download_blocked;
  const copyBlocked = !!restrictionsQ.data?.copy_blocked;

  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfBlobLoading, setPdfBlobLoading] = useState(false);
  const [pdfBlobError, setPdfBlobError] = useState<string | null>(null);
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
    && !(window as unknown as { MSStream?: unknown }).MSStream;
  const isAndroid = /Android/i.test(userAgent);
  // TWA и некоторые встроенные Android-браузеры урезают/маскируют User-Agent.
  // Поэтому одного слова "Android" недостаточно: сенсорный узкий экран тоже
  // считаем мобильным. Это не затрагивает обычные desktop-браузеры и больше не
  // отдаёт мобильным устройствам ненадёжный встроенный PDF iframe.
  const isCoarsePointer = typeof window !== "undefined"
    && window.matchMedia?.("(pointer: coarse)").matches;
  const isNarrowViewport = typeof window !== "undefined" && window.innerWidth <= 1024;
  const isMobileDevice = isAndroid || isIOS || (isCoarsePointer && isNarrowViewport);
  // PDF и Office показываем постранично (серверный рендер) НА ВСЕХ устройствах
  // (25.09.2026). iframe со встроенным просмотрщиком браузера подводил везде:
  // iOS — одна страница, Android Chrome — пусто, Яндекс.Браузер — «Сайт
  // заблокирован», Firefox с «сохранять PDF» — скачивание вместо показа и пустая
  // модалка (владелец: «на компьютере ничего не открывается»). Родной
  // просмотрщик остаётся доступен кнопкой «Открыть в новой вкладке».
  const usePagesViewer = (kind === "pdf" || kind === "office") && attachmentId != null;
  void isMobileDevice;
  // YaBrowser рендерит PDF через внутренний viewer с другим origin и блокирует
  // даже наш SAMEORIGIN-ответ внутри iframe. Blob URL не несёт frame-заголовки,
  // при этом байты по-прежнему получает только авторизованный fetch.
  const needsPdfBlob = (kind === "pdf" || kind === "office") && !usePagesViewer && /YaBrowser|YandexBrowser/i.test(userAgent);
  // Что именно тянуть в blob: для Office — серверную PDF-конвертацию.
  const blobSourceUrl = kind === "office" ? officePreviewUrl(url) : url;

  // Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Подгрузка content для text (только в режиме view).
  // PDF/office рендерятся через iframe — браузер сам.
  useEffect(() => {
    if (mode !== "view") return;
    if (kind !== "text") { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const txt = await res.text();
        if (!cancelled) { setContent(txt); setLoading(false); }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Не удалось загрузить файл";
        if (!cancelled) { setError(msg); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, kind, url]);

  // Совместимость с мобильным Яндекс.Браузером: его встроенный PDF viewer
  // показывает «Сайт заблокирован» для защищённого SAMEORIGIN iframe.
  useEffect(() => {
    if (mode !== "view" || !needsPdfBlob) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    let cancelled = false;
    setPdfBlobLoading(true);
    setPdfBlobError(null);
    setPdfBlobUrl(null);
    void (async () => {
      try {
        const res = await fetch(blobSourceUrl, { credentials: "include", signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objectUrl);
      } catch (e: unknown) {
        if (controller.signal.aborted || cancelled) return;
        setPdfBlobError(e instanceof Error ? e.message : "Не удалось загрузить PDF");
      } finally {
        if (!cancelled) setPdfBlobLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mode, needsPdfBlob, blobSourceUrl]);

  const navigate = useNavigate();
  // «Редактировать»: docx → SuperDoc (/pages/word, файл как есть, сохранение в оригинал),
  // xlsx → «Страницы» (/pages/open — таблицы по листам).
  const openEditor = () => {
    if (!canEdit || attachmentId == null) return;
    onClose();
    if (IN_CRM) {
      openInMessenger(`/pages/${/\.(docx|pdf)$/i.test(filename) ? "word" : "sheet"}/${attachmentId}`);
      return;
    }
    if (/\.(docx|pdf)$/i.test(filename)) {
      void navigate({ to: "/pages/word/$attId", params: { attId: String(attachmentId) } });
    } else {
      void navigate({ to: "/pages/sheet/$attId", params: { attId: String(attachmentId) } });
    }
  };

  const typeColor = fileTypeColor(filename);
  const typeLabel = fileTypeLabel(filename);

  return (
    <div className="file-preview-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={`file-preview-modal${mode === "choice" ? " is-choice" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="file-preview-head file-preview-head-2rows">
          {/* Row 1 — название + × */}
          <div className="file-preview-head-row1">
            <div className="file-preview-title" title={filename}>
              <span className="file-preview-type-badge" style={{ background: typeColor }}>{typeLabel}</span>
              <span className="file-preview-name">{filename}</span>
            </div>
            <button
              type="button"
              className="btn btn-sm file-preview-close"
              onClick={onClose}
              title="Закрыть (Esc)"
              aria-label="Закрыть"
            >
              <Icon name="x" size={14}/>
            </button>
          </div>
          {/* Row 2 — действия */}
          <div className="file-preview-head-row2">
            {attachmentId != null && (
              <RestrictionsButton attachmentId={attachmentId}/>
            )}
            <a
              className="btn btn-sm btn-secondary"
              href={forceDownloadUrl(url)}
              download={filename}
              title="Скачать"
              onClick={(e) => { e.stopPropagation(); if (downloadBlocked) { e.preventDefault(); alert("Скачивание этого документа запрещено модератором."); } }}
              style={downloadBlocked ? { opacity: 0.4, pointerEvents: "auto" } : undefined}
            >
              <Icon name="download" size={14}/>
            </a>
            {(kind === "pdf" || kind === "office") && (
              <a
                className="btn btn-sm btn-secondary file-preview-open-tab"
                href={kind === "office" ? officePreviewUrl(url) : url}
                target="_blank"
                rel="noopener noreferrer"
                title="Открыть в новой вкладке (встроенный просмотрщик браузера)"
                onClick={(e) => e.stopPropagation()}
              >
                <Icon name="external" size={14}/>
              </a>
            )}
            {onSaveToDrive && attachmentId != null && (
              <button type="button" className="btn btn-sm btn-secondary"
                onClick={(e) => { e.stopPropagation(); onSaveToDrive({ attachmentId, filename }); }}
                title="Добавить на диск">
                <Icon name="folder" size={14}/>
              </button>
            )}
            {attachmentId != null && (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={(e) => { e.stopPropagation(); setRenameOpen(true); }}
                title="Переименовать документ"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Icon name="edit" size={14}/>
                <span>Переименовать</span>
              </button>
            )}
            {isManagedDocument && attachmentId != null && !copyBlocked && (
              <CopyButton
                attachmentId={attachmentId}
                onSuccess={(newAid, newName) => {
                  if (onCopied) onCopied(newAid, newName);
                  // Копия: docx → SuperDoc, xlsx → «Страницы»
                  onClose();
                  if (IN_CRM) {
                    openInMessenger(`/pages/${/\.docx$/i.test(filename) ? "word" : "sheet"}/${newAid}`);
                    return;
                  }
                  if (/\.docx$/i.test(filename)) {
                    void navigate({ to: "/pages/word/$attId", params: { attId: String(newAid) } });
                  } else {
                    void navigate({ to: "/pages/sheet/$attId", params: { attId: String(newAid) } });
                  }
                }}
              />
            )}
            {onDelete && (
              <button
                type="button"
                className="btn btn-sm btn-secondary file-preview-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Удалить «${filename}»? Документ переедет в корзину Диска и будет храниться 30 дней.`)) {
                    onDelete();
                  }
                }}
                title="Удалить документ (в корзину Диска)"
                aria-label="Удалить"
              >
                <Icon name="trash" size={14}/>
              </button>
            )}
          </div>
        </div>

        {/* CHOICE MODE — карточка с двумя действиями */}
        {mode === "choice" && (
          <div className="file-preview-choice">
            <div className="file-preview-choice-icon" style={{ background: typeColor }}>
              {typeLabel}
            </div>
            <div className="file-preview-choice-name">{filename}</div>
            <div className="file-preview-choice-meta">
              {fmtSize(sizeBytes ?? null)}
            </div>
            <div className="file-preview-choice-actions">
              <button type="button" className="file-preview-choice-btn-view" onClick={() => setMode("view")}>
                <Icon name="eye" size={18}/>
                <span>Посмотреть</span>
              </button>
              <button type="button" className="file-preview-choice-btn-edit" onClick={openEditor}>
                <Icon name="edit" size={18}/>
                <span>{/\.pdf$/i.test(filename) ? "Открыть в редакторе" : "Редактировать"}</span>
              </button>
            </div>
            <div className="file-preview-choice-hint">
              {/\.docx$/i.test(filename)
                ? "«Редактировать» откроет документ Word как есть: оформление сохраняется, правки пишутся в этот же файл с историей версий"
                : /\.pdf$/i.test(filename)
                  ? "Откроется в редакторе документов постранично, с масштабом и поиском"
                  : "«Редактировать» откроет таблицу Excel как есть: листы, формулы и оформление, правки пишутся в этот же файл с историей версий"}
            </div>
          </div>
        )}

        {/* VIEW MODE — встроенный preview */}
        {mode === "view" && (
          <div
            className="file-preview-body"
            // Постраничный просмотрщик — flex-колонка: лента занимает остаток
            // высоты, а «Открыть в редакторе» всегда видна снизу, а не под лентой.
            style={usePagesViewer ? { display: "flex", flexDirection: "column" } : undefined}
          >
            {(loading || pdfBlobLoading) && (
              <div style={{ padding: 60, display: "grid", placeItems: "center" }}>
                <Spinner size="md" label="Загрузка…"/>
              </div>
            )}

            {!loading && !pdfBlobLoading && (error || pdfBlobError) && (
              <div style={{ padding: 40, color: "var(--brick)", textAlign: "center" }}>
                {error || pdfBlobError}<br/>
                <a className="btn btn-sm" href={url} download={filename} style={{ marginTop: 12, display: "inline-flex" }}>
                  <Icon name="download" size={14}/> Скачать
                </a>
              </div>
            )}

            {!loading && !pdfBlobLoading && !error && !pdfBlobError && kind === "pdf" && (!needsPdfBlob || pdfBlobUrl) && (() => {
              if (usePagesViewer && attachmentId != null) {
                return (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <DocPagesViewer
                      attachmentId={attachmentId}
                      filename={filename}
                      downloadUrl={url}
                    />
                  </div>
                );
              }
              // iOS Safari в <iframe> для PDF показывает ТОЛЬКО ПЕРВУЮ
              // страницу — известное ограничение нативного PDF-viewer'а.
              // Для iOS показываем кнопку «Открыть в новой вкладке» (там
              // мобильная Safari отрендерит все страницы корректно).
              if (isIOS) {
                return (
                  <div style={{ display: "grid", placeItems: "center", padding: 32, gap: 16 }}>
                    <div style={{ fontSize: 64 }}>📄</div>
                    <div style={{ fontSize: 14, color: "var(--mute)", textAlign: "center" }}>
                      На iPhone многостраничный PDF удобнее открыть в новой вкладке Safari.
                    </div>
                    <a className="btn btn-primary" href={url} target="_blank" rel="noopener noreferrer">
                      <Icon name="external" size={14}/> Открыть в новой вкладке
                    </a>
                    <a className="btn btn-sm btn-secondary" href={url} download={filename}>
                      <Icon name="download" size={14}/> Скачать
                    </a>
                  </div>
                );
              }
              return <iframe src={needsPdfBlob ? pdfBlobUrl! : url} className="file-preview-pdf" title={filename}/>;
            })()}
            {!loading && !error && kind === "text" && (
              <pre className="file-preview-text">{content}</pre>
            )}
            {!loading && !pdfBlobLoading && !error && !pdfBlobError && kind === "office" && (!needsPdfBlob || pdfBlobUrl) && (
              usePagesViewer && attachmentId != null
                ? (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <DocPagesViewer
                      attachmentId={attachmentId}
                      filename={filename}
                      downloadUrl={url}
                    />
                  </div>
                )
                : <iframe src={needsPdfBlob ? pdfBlobUrl! : officePreviewUrl(url)} className="file-preview-pdf" title={filename}/>
            )}
            {!loading && !error && kind === "spreadsheet" && (
              // Таблицы читаем в браузере (SheetJS): листы вкладками, объединения ячеек.
              // OnlyOffice больше не используется.
              <SheetViewer url={url} filename={filename} />
            )}
            {!loading && kind === "other" && (
              <div style={{ padding: 40, color: "var(--mute)", textAlign: "center" }}>
                Этот тип файла нельзя показать в браузере.<br/>
                <a className="btn btn-sm" href={url} download={filename} style={{ marginTop: 12, display: "inline-flex" }}>
                  <Icon name="download" size={14}/> Скачать
                </a>
              </div>
            )}

            {canEdit && (
              <div className="file-preview-view-edit-bar">
                <button type="button" className="file-preview-choice-btn-edit" onClick={openEditor}>
                  <Icon name="edit" size={16}/> Открыть в редакторе
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {renameOpen && attachmentId != null && (
        <RenameModal
          attachmentId={attachmentId}
          currentFilename={filename}
          onClose={() => setRenameOpen(false)}
          onDone={(newName) => {
            setRenameOpen(false);
            if (onRenamed) onRenamed(newName);
          }}
        />
      )}
    </div>
  );
}


function RenameModal({ attachmentId, currentFilename, onClose, onDone }: {
  attachmentId: number;
  currentFilename: string;
  onClose: () => void;
  onDone: (newFilename: string) => void;
}) {
  // По умолчанию — имя БЕЗ расширения, курсор в конце. Расширение возвращает backend
  // автоматически если юзер его убрал.
  const dotIdx = currentFilename.lastIndexOf(".");
  const baseInit = dotIdx > 0 ? currentFilename.slice(0, dotIdx) : currentFilename;
  const ext = dotIdx > 0 ? currentFilename.slice(dotIdx) : "";
  const [name, setName] = useState(baseInit);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (n: string) => renameAttachment(attachmentId, n),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["docs/list"] });
      qc.invalidateQueries({ queryKey: ["chat/messages"] });
      onDone(res.filename);
    },
    onError: (e: Error) => setErr(e.message || "Не удалось переименовать"),
  });
  return (
    <div className="kb-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="kb-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="kb-modal-title">
          <span className="kb-modal-title-emoji" aria-hidden>✏️</span>
          Переименовать документ
        </div>
        <div className="kb-modal-field">
          <label className="kb-modal-label">Новое имя</label>
          <div className="kb-modal-input-wrap">
            <input
              autoFocus
              value={name}
              onChange={(e) => { setName(e.target.value); setErr(null); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) mut.mutate(name.trim());
                if (e.key === "Escape") onClose();
              }}
              placeholder="Введите новое имя"
              className="kb-modal-input"
            />
            {ext && <span className="kb-modal-input-suffix">{ext}</span>}
          </div>
          <div className="kb-modal-hint">Расширение сохраняется автоматически.</div>
        </div>
        {err && <div className="kb-modal-err">{err}</div>}
        <div className="kb-modal-actions">
          <button type="button" className="kb-btn kb-btn-ghost" onClick={onClose} disabled={mut.isPending}>Отмена</button>
          <button
            type="button"
            className="kb-btn kb-btn-primary"
            disabled={mut.isPending || !name.trim() || name.trim() === currentFilename}
            onClick={() => mut.mutate(name.trim())}
          >
            {mut.isPending ? "Сохраняю…" : "Переименовать"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CopyButton({ attachmentId, onSuccess }: {
  attachmentId: number;
  onSuccess: (newAttachmentId: number, newFilename: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-sm btn-secondary"
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
          const res = await copyDoc(attachmentId);
          onSuccess(res.attachment_id, res.filename);
        } catch (err) {
          alert(`Не удалось скопировать: ${(err as Error).message ?? err}`);
        } finally {
          setBusy(false);
        }
      }}
      title="Создать копию и открыть в редакторе"
    >
      {busy ? "…" : <Icon name="copy" size={14}/>}
    </button>
  );
}


/**
 * RestrictionsButton — иконка-замок 🔒 в шапке FilePreviewModal.
 *
 *   - Если запретов нет — показывает 🔓 серым (открытый замок). Клик
 *     открывает мини-меню «Запретить скачивание / Запретить копирование».
 *   - Если запрет активен — показывает 🔒 красным. Hover/клик показывает что
 *     запрещено + кнопку «Снять запрет».
 *   - Тап у обычного юзера → информационная подсказка. PATCH вернёт 403,
 *     если прав нет — обработаем ошибку.
 */
function RestrictionsButton({ attachmentId }: { attachmentId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["docs/restrictions", attachmentId],
    queryFn: () => fetchDocRestrictions(attachmentId),
    staleTime: 30_000,
  });
  const setMut = useMutation({
    mutationFn: (p: { download_blocked: boolean; copy_blocked: boolean }) =>
      setDocRestrictions(attachmentId, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docs/restrictions", attachmentId] }),
    onError: (e: Error) => alert(e.message || "Не удалось изменить запреты"),
  });
  const dl = !!q.data?.download_blocked;
  const cp = !!q.data?.copy_blocked;
  const anyBlocked = dl || cp;
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="btn btn-sm btn-secondary"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        title={anyBlocked
          ? `Запреты: ${dl ? "скачивание" : ""}${dl && cp ? ", " : ""}${cp ? "копирование" : ""}`
          : "Управление запретами"}
        style={{
          color: anyBlocked ? "#d92d20" : "var(--ink-3)",
          justifyContent: "center",
          padding: "6px 8px",
        }}
        aria-pressed={anyBlocked}
      >
        <Icon name={anyBlocked ? "lock" : "lock_open"} size={14}/>
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            // Кнопка замка — самая ЛЕВАЯ в row2. Popover открываем
            // ВПРАВО (left:0), а не right:0 — иначе уходит за левый край
            // модалки и обрезается overflow:hidden.
            position: "absolute", top: "calc(100% + 6px)", left: 0,
            zIndex: 200, minWidth: 220,
            background: "#fff", border: "1px solid var(--line)",
            borderRadius: 8, padding: 8,
            boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ fontSize: 11.5, color: "var(--mute)", padding: "2px 6px 6px", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Запреты документа
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 6px", cursor: "pointer", borderRadius: 6 }}>
            <input
              type="checkbox"
              checked={dl}
              disabled={setMut.isPending}
              onChange={(e) => setMut.mutate({ download_blocked: e.target.checked, copy_blocked: cp })}
            />
            <span style={{ fontSize: 13 }}>Запретить скачивание</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 6px", cursor: "pointer", borderRadius: 6 }}>
            <input
              type="checkbox"
              checked={cp}
              disabled={setMut.isPending}
              onChange={(e) => setMut.mutate({ download_blocked: dl, copy_blocked: e.target.checked })}
            />
            <span style={{ fontSize: 13 }}>Запретить копирование</span>
          </label>
          <div style={{ fontSize: 11, color: "var(--mute)", padding: "8px 6px 2px", lineHeight: 1.35 }}>
            Запреты действуют везде: в чате, на Диске и в редакторе документов.
          </div>
        </div>
      )}
    </div>
  );
}
